import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import FormField from '@/shared/components/forms/WaveUIForm_V2/types/formField.type';
import MetadataTypeDefinition from '@/shared/types/metadata/metadataTypeDefinition.type';

const metadataFieldsConfig: {
  [key in keyof MetadataTypeDefinition]: (Omit<FormField, 'field_props'> & {
    field_props: { [key: string]: unknown };
  })[];
} = {
  boolean: [
    {
      field_type: FieldType.Switch,
      field_props: {
        required: 'required',
        name: 'fieldName',
        label: 'fieldLabel',
        helperText: 'fieldHelperText',
        checked: 'value',
      },
    },
  ],
  color: [
    {
      field_type: FieldType.ColorPicker,
      field_props: {
        required: 'required',
        name: 'fieldName',
        label: 'fieldLabel',
        value: 'value',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
      },
    },
  ],
  date: [
    {
      field_type: FieldType.Date,
      field_props: {
        required: 'required',
        name: 'fieldName',
        label: 'fieldLabel',
        value: 'value',
        helperText: 'fieldHelperText',
      },
    },
  ],
  dimension: [
    {
      columns: 6,
      field_type: FieldType.Select,
      field_props: {
        required: 'required',
        name: 'fieldName:dimension_unit',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        value: 'value',
        helperText: 'fieldHelperText',
        options: [
          { label: 'mm', value: 'mm' },
          { label: 'cm', value: 'cm' },
          { label: 'm', value: 'm' },
          { label: 'km', value: 'km' },
          { label: 'in', value: 'in' },
          { label: 'ft', value: 'ft' },
          { label: 'yd', value: 'yd' },
        ],
      },
    },
    {
      columns: 6,
      field_type: FieldType.Number,
      field_props: {
        required: 'required',
        name: 'fieldName:dimension_value',
        label: 'fieldLabel',
        value: 'value',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
      },
    },
  ],
  integer: [
    {
      field_type: FieldType.Number,
      field_props: {
        required: 'required',
        name: 'fieldName',
        label: 'fieldLabel',
        value: 'value',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
      },
    },
  ],
  json: [
    {
      field_type: FieldType.Text,
      field_props: {
        required: 'required',
        name: 'fieldName',
        label: 'fieldLabel',
        value: 'value',
        placeholder: 'fieldPlaceholder',
        multiline: true,
        rows: 4,
        helperText: 'fieldHelperText',
      },
    },
  ],
  money: [
    {
      columns: 6,
      field_type: FieldType.Select,
      field_props: {
        required: 'required',
        name: 'fieldName:currency_code',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
        value: 'value',
        options: [
          { label: 'USD', value: 'USD' },
          { label: 'EUR', value: 'EUR' },
          { label: 'CAD', value: 'CAD' },
        ],
      },
    },
    {
      columns: 6,
      field_type: FieldType.Number,
      field_props: {
        required: 'required',
        name: 'fieldName:amount',
        label: 'fieldLabel',
        value: 'value',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
      },
    },
  ],
  multi_line_text_field: [
    {
      field_type: FieldType.Text,
      field_props: {
        required: 'required',
        name: 'fieldName',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
        multiline: true,
        value: 'value',
        rows: 4,
      },
    },
  ],
  numeric: [
    {
      field_type: FieldType.Number,
      field_props: {
        required: 'required',
        name: 'fieldName',
        label: 'fieldLabel',
        value: 'value',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
      },
    },
  ],
  rating: [
    {
      columns: 4,
      field_type: FieldType.Number,
      field_props: {
        required: 'required',
        name: 'fieldName:scale_max',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
        value: 'value',
      },
    },
    {
      columns: 4,
      field_type: FieldType.Number,
      field_props: {
        required: 'required',
        name: 'fieldName:scale_min',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
        value: 'value',
      },
    },
    {
      columns: 4,
      field_type: FieldType.Number,
      field_props: {
        required: 'required',
        name: 'fieldName:value',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
        value: 'value',
      },
    },
  ],
  rich_text_field: [
    {
      field_type: FieldType.Text,
      field_props: {
        required: 'required',
        name: 'fieldName',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
        multiline: true,
        value: 'value',
        rows: 4,
      },
    },
  ],
  single_line_text_field: [
    {
      field_type: FieldType.Text,
      field_props: {
        required: 'required',
        name: 'fieldName',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        value: 'value',
        helperText: 'fieldHelperText',
      },
    },
  ],
  string_integer: [
    {
      field_type: FieldType.Number,
      field_props: {
        required: 'required',
        name: 'fieldName',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        value: 'value',
        helperText: 'fieldHelperText',
      },
    },
  ],
  timestamp: [
    {
      field_type: FieldType.DateTime,
      field_props: {
        required: 'required',
        name: 'fieldName',
        label: 'fieldLabel',
        value: 'value',
        helperText: 'fieldHelperText',
      },
    },
  ],
  timezone: [
    {
      field_type: FieldType.Number,
      field_props: {
        required: 'required',
        name: 'fieldName',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        value: 'value',
        helperText: 'fieldHelperText',
      },
    },
  ],
  url: [
    {
      field_type: FieldType.Text,
      field_props: {
        required: 'required',
        name: 'fieldName',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        value: 'value',
        helperText: 'fieldHelperText',
      },
    },
  ],
  volume: [
    {
      columns: 6,
      field_type: FieldType.Select,
      field_props: {
        required: 'required',
        name: 'fieldName:unit',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
        value: 'value',
        options: [
          { label: 'ml', value: 'ml' },
          { label: 'g', value: 'g' },
        ],
      },
    },
    {
      columns: 6,
      field_type: FieldType.Number,
      field_props: {
        required: 'required',
        name: 'fieldName:value',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
        value: 'value',
      },
    },
  ],
  weight: [
    {
      columns: 6,
      field_type: FieldType.Select,
      field_props: {
        required: 'required',
        name: 'fieldName:unit',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
        value: 'value',
        options: [{ label: 'g', value: 'g' }],
      },
    },
    {
      columns: 6,
      field_type: FieldType.Number,
      field_props: {
        required: 'required',
        name: 'fieldName:value',
        label: 'fieldLabel',
        placeholder: 'fieldPlaceholder',
        helperText: 'fieldHelperText',
        value: 'value',
      },
    },
  ],
};

export default metadataFieldsConfig;
